import { type ReactElement, useCallback } from 'react'
import { NotReady } from 'app/components/NotReady'
import { ButtonSave } from 'app/components/buttons'
import { type TeamData, type TeamGetSuccess, type TeamId } from '@terros/common'
import { useForm } from '@mantine/form'
import { Group, Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import { omitLockedTeamFields, useLockedTeamFields, useTeam, useUpdateTeam } from '@front/model'
import { RestoreTeamButton } from '../RestoreTeamButton'
import { TeamDangerZone } from './TeamDangerZone'
import { EditTeamDrawerForm } from './EditDrawer'

type Props = {
  teamId: TeamId
  onSaved?: VoidFunction
}

export const TeamEditFormPanel = ({ teamId, onSaved }: Props): ReactElement => {
  const { data, isLoading, error } = useTeam(teamId)

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='team' isLoading={isLoading} />

  if (data.team.isDeleted) return <RestoreTeamButton teamId={teamId} />

  return <LoadedTeamEditForm data={data} onSaved={onSaved} />
}

const LoadedTeamEditForm = ({ data, onSaved }: { data: TeamGetSuccess; onSaved?: VoidFunction }): ReactElement => {
  const form = useForm<TeamData>({
    initialValues: data.team,
  })
  const { update, isUpdating, error: updateError } = useUpdateTeam()
  const { lockedFields, isLoading: isLoadingLocks } = useLockedTeamFields(data.team.teamId)
  useToastError(updateError)

  const handleSave = useCallback(
    async (values: TeamData): Promise<void> => {
      if (isLoadingLocks) return
      await update(omitLockedTeamFields(values, lockedFields))
      onSaved?.()
    },
    [onSaved, update, lockedFields, isLoadingLocks]
  )

  return (
    <form onSubmit={form.onSubmit(handleSave)}>
      <Stack>
        <EditTeamDrawerForm form={form} />
        <TeamDangerZone form={form} teamData={data} />
        <Group>
          <ButtonSave disabled={isLoadingLocks} loading={isUpdating} type='submit'>
            Save
          </ButtonSave>
        </Group>
      </Stack>
    </form>
  )
}
